import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

export function useExistValidator(originalValidator, delay = 500) {
  const isSubmittingRef = useRef(false);
  const validatedCacheRef = useRef(new Set());

  const debouncedValidator = useRef(
    debounce((resolve, reject, args) => {
      originalValidator(...args)
        .then((res) => {
          const value = args[1]?.trim?.();
          if (value) {
            validatedCacheRef.current.add(value);
          }
          resolve(res);
        })
        .catch(reject);
    }, delay)
  ).current;

  const validator = useCallback(
    (...args) => {
      const value = args[1]?.trim?.();

      if (!value) return Promise.resolve();

      if (validatedCacheRef.current.has(value)) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        if (isSubmittingRef.current) {
          originalValidator(...args)
            .then((res) => {
              validatedCacheRef.current.add(value);
              resolve(res);
            })
            .catch(reject);
        } else {
          debouncedValidator(resolve, reject, args);
        }
      });
    },
    [debouncedValidator, originalValidator]
  );

  const reset = () => {
    validatedCacheRef.current = new Set();
  };

  const addToCache = (value) => {
    if (value?.trim()) validatedCacheRef.current.add(value.trim());
  };

  return { validator, isSubmittingRef, reset, addToCache };
}

// Cache response của useAsyncData (theo cacheKey), giới hạn số entry, đầy thì bỏ entry cũ nhất
const CACHE_MAX_ENTRIES = 100;
const responseCache = new Map();

const writeCache = (key, data) => {
  responseCache.delete(key);
  responseCache.set(key, data);
  if (responseCache.size > CACHE_MAX_ENTRIES) responseCache.delete(responseCache.keys().next().value);
};

// Xoá cache khi đăng xuất (đăng nhập lại tài khoản khác không thấy số liệu cũ)
export const clearAsyncDataCache = () => responseCache.clear();

// JSON.stringify với key của object được sắp xếp: { a, b } và { b, a } ra cùng 1 chuỗi
const stableStringify = (value) =>
  JSON.stringify(value, (_, item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return item;
    const sorted = Object.keys(item).sort();
    return Object.fromEntries(sorted.map((key) => [key, item[key]]));
  });

// Tạo cacheKey cho useAsyncData: tên API + tham số (thứ tự key không quan trọng)
export const asyncDataKey = (name, ...params) => `${name}:${stableStringify(params)}`;

/**
 * Gọi API mỗi khi deps thay đổi. Response của lần gọi cũ về muộn sẽ bị bỏ qua (tránh race condition),
 * data cũ được giữ trong lúc tải để giao diện không nhấp nháy.
 * fetcher trả về falsy khi chưa đủ điều kiện gọi (VD chưa chọn app).
 *
 * options.cacheKey (tạo bằng asyncDataKey): stale-while-revalidate, quay lại màn / bộ lọc đã xem thì hiện ngay
 * data trong cache (loading = false, refreshing = true) rồi vẫn gọi API để cập nhật.
 * Gọi lại cùng cacheKey (VD bấm tải lại / thử lại) thì loading như bình thường.
 * Không dùng cho dữ liệu realtime / export.
 */
export function useAsyncData(fetcher, deps, { cacheKey } = {}) {
  const [state, setState] = useState({ data: undefined, loading: false, error: undefined });
  // Có cache cho key mới (lần render đầu / vừa đổi bộ lọc): trả luôn data cache, không chờ effect
  const cached = cacheKey && cacheKey !== state.cacheKey ? responseCache.get(cacheKey) : undefined;

  useEffect(() => {
    const request = fetcher();
    if (!request) {
      setState({ data: undefined, loading: false, error: undefined, ...(cacheKey && { cacheKey }) });
      return undefined;
    }

    let active = true;
    if (cached !== undefined) {
      setState({ data: cached, loading: false, refreshing: true, error: undefined, cacheKey });
    } else {
      setState((prev) => ({ ...prev, loading: true, error: undefined }));
    }
    request
      .then((data) => {
        // Vẫn lưu cache dù đã đổi bộ lọc: data vẫn đúng với key của nó
        if (cacheKey) writeCache(cacheKey, data);
        if (active) setState({ data, loading: false, error: undefined, ...(cacheKey && { cacheKey }) });
      })
      .catch((error) => active && setState({ data: undefined, loading: false, error, ...(cacheKey && { cacheKey }) }));

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return cached !== undefined ? { data: cached, loading: false, refreshing: true, error: undefined } : state;
}
